const ROOM_FILTER_ENTRY_HASH = '#room-filter';
const ROOMS_FILTER_PARAM_NAME = 'filter-p-m-custom-rooms';

class UlifCollectionFilters extends HTMLElement {
  /**
   * Legacy safety net for any non-facet Section Rendering morph that reaches
   * this component. Normal Ulif filter submissions no longer morph this shell:
   * they replace only #ResultsList. If another theme path does morph the whole
   * section, retain the user's panel/accordion state rather than letting server
   * defaults collapse or expand it.
   */
  initMorphObserver() {
    if (this._morphObserver) return;
    this._morphObserver = new MutationObserver((mutations) => {
      // Right-only filter rendering may refresh the small server-derived
      // metadata slots (count / active chips / Clear all) inside this stable
      // shell. Those are intentional local updates, not a section morph, and
      // must not re-run panel/accordion restoration.
      if (mutations.length && mutations.every((mutation) => this.isMetadataMutation(mutation))) return;
      if (this._restoring) return;
      this._restoring = true;
      queueMicrotask(() => {
        this._restoring = false;
        if (!this.isConnected) return;
        this.afterMorph();
      });
    });
    this._morphObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-panel-open', 'data-ulif-panel-visible', 'hidden'],
    });
  }

  isMetadataMutation(mutation) {
    const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
    return !!target?.closest(
      '[data-ulif-active-count], [data-ulif-active-filters], [data-ulif-product-count], [data-ulif-mobile-status]'
    );
  }

  /**
   * FilterUpdateEvent fires before the Section Rendering API response comes
   * back, so the component's own MutationObserver also sees local form-input
   * changes before a morph. Observe the results list separately and only
   * consume the pending animation after the product-grid subtree itself has
   * changed. MutationObserver callbacks run after synchronous morph work, and
   * the following frame gives the newly morphed cards a stable first paint.
   */
  initGridMorphObserver() {
    const liveResultsList = this.closest('results-list');
    if (!liveResultsList || this._gridMorphObserverTarget === liveResultsList) return;

    this._gridMorphObserver?.disconnect();
    this._gridMorphObserverTarget = liveResultsList;
    this._gridMorphObserver = new MutationObserver((mutations) => {
      if (!this._awaitingMobileGridEntrance || !this.shouldUseMobileGridEntrance()) return;
      if (!this.didProductGridChange(mutations)) return;

      this._awaitingMobileGridEntrance = false;
      this.queueGridEntranceAnimation();
    });
    this._gridMorphObserver.observe(liveResultsList, {
      childList: true,
      subtree: true,
    });
  }

  didProductGridChange(mutations) {
    const mainGrid = this.resultsList?.querySelector('.main-collection-grid');

    return mutations.some((mutation) => {
      if (mainGrid?.contains(mutation.target)) return true;

      return [...mutation.addedNodes, ...mutation.removedNodes].some(
        (node) =>
          node instanceof Element &&
          (node.matches('.main-collection-grid') || node.querySelector('.main-collection-grid'))
      );
    });
  }

  /**
   * Mark just mobile, open-panel filter submissions for the ordinary CSS
   * fallback. Closed-panel and desktop updates retain the theme's view
   * transition path and never receive this class.
   */
  handleFilterUpdate = () => {
    this._awaitingMobileGridEntrance = this.shouldUseMobileGridEntrance();
  };

  shouldUseMobileGridEntrance() {
    return (
      this.mobileMedia?.matches &&
      this.isPanelOpen &&
      this.panel?.hasAttribute('data-ulif-panel-visible') &&
      !this.prefersReducedMotion()
    );
  }

  queueGridEntranceAnimation() {
    window.cancelAnimationFrame(this.gridEntranceAnimationFrame);
    this.gridEntranceAnimationFrame = window.requestAnimationFrame(() => {
      this.gridEntranceAnimationFrame = null;
      if (!this.shouldUseMobileGridEntrance()) return;

      const mainGrid = this.resultsList?.querySelector('.main-collection-grid');
      if (mainGrid) this.playGridEntranceAnimation(mainGrid);
    });
  }

  playGridEntranceAnimation(mainGrid) {
    const cards = Array.from(mainGrid.querySelectorAll('.product-grid__item')).slice(0, 8);
    if (cards.length === 0) return;

    this.cleanupGridEntranceAnimation();
    mainGrid.classList.remove('main-collection-grid--ulif-entering');
    // Restart the one-shot keyframes when a user applies another filter before
    // the previous grid animation's cleanup has happened.
    mainGrid.offsetHeight;
    mainGrid.classList.add('main-collection-grid--ulif-entering');

    const lastAnimatedCard = cards.at(-1);
    const handleAnimationEnd = (event) => {
      if (event.target !== lastAnimatedCard || event.animationName !== 'ulif-product-grid-card-enter') return;
      this.cleanupGridEntranceAnimation();
    };

    this.gridEntranceAnimationGrid = mainGrid;
    this.gridEntranceAnimationTarget = lastAnimatedCard;
    this.gridEntranceAnimationEndHandler = handleAnimationEnd;
    lastAnimatedCard.addEventListener('animationend', handleAnimationEnd);
  }

  cleanupGridEntranceAnimation() {
    this.gridEntranceAnimationTarget?.removeEventListener('animationend', this.gridEntranceAnimationEndHandler);
    this.gridEntranceAnimationGrid?.classList.remove('main-collection-grid--ulif-entering');
    this.gridEntranceAnimationGrid = null;
    this.gridEntranceAnimationTarget = null;
    this.gridEntranceAnimationEndHandler = null;
  }

  afterMorph() {
    this.rebind();
    // The morph can *replace* the whole panel subtree (not just patch it in
    // place): replaced accordions never pass through morph's onBeforeUpdate,
    // so they lack the data-ulf-no-animate suppression marker and would play
    // their @starting-style expand animation right after swap-in (the panel
    // looks like it re-renders/re-expands once). Mark every accordion here so
    // restoreAccordionState can release them together next frames.
    this.querySelectorAll('.ulif-collection-filters__facets accordion-custom').forEach((accordion) =>
      accordion.setAttribute('data-ulf-no-animate', '')
    );
    this.restoreAccordionState();

    if (!this.isPanelOpen) return;

    const desktopNeedsRestore = !this.mobileMedia.matches && !this.hasAttribute('data-panel-open');
    const mobileNeedsRestore =
      this.mobileMedia.matches &&
      (this.panel?.hidden || !this.panel?.hasAttribute('data-ulif-panel-visible'));
    if (desktopNeedsRestore || mobileNeedsRestore) {
      this.setOpen(true, { animate: false, persist: false });
    }
  }

  /**
   * Copy only the small pieces of server-derived filter metadata that cannot
   * be inferred from a live form (active chips, Clear all URLs, active count,
   * and result count). The panel and facet inputs are deliberately excluded:
   * they are client-owned and must keep their DOM identity.
   *
   * @param {HTMLElement | null} incomingFilters
   */
  syncServerMetadata(incomingFilters) {
    if (!(incomingFilters instanceof HTMLElement)) return;

    const sourceFor = (attribute, value) =>
      Array.from(incomingFilters.querySelectorAll(`[${attribute}]`)).find(
        (element) => element.getAttribute(attribute) === value
      );

    this.querySelectorAll('[data-ulif-active-filters]').forEach((current) => {
      const source = sourceFor('data-ulif-active-filters', current.getAttribute('data-ulif-active-filters'));
      current.innerHTML = source?.innerHTML ?? '';
    });

    this.querySelectorAll('[data-ulif-active-count]').forEach((current) => {
      const source = sourceFor('data-ulif-active-count', current.getAttribute('data-ulif-active-count'));
      current.textContent = source?.textContent ?? '';
      current.toggleAttribute('hidden', source?.hidden ?? true);
      if (source?.hasAttribute('aria-label')) current.setAttribute('aria-label', source.getAttribute('aria-label'));
    });

    this.querySelectorAll('[data-ulif-product-count]').forEach((current) => {
      const source = sourceFor('data-ulif-product-count', current.getAttribute('data-ulif-product-count'));
      if (source) current.innerHTML = source.innerHTML;
    });

    this.querySelectorAll('[data-ulif-mobile-status]').forEach((current) => {
      const source = incomingFilters.querySelector('[data-ulif-mobile-status]');
      current.toggleAttribute('hidden', source?.hidden ?? true);
    });
  }

  /**
   * Keep track of the accordions the user manually expanded/collapsed.
   */
  handleAccordionToggle = (event) => {
    const accordion = event.target.closest('accordion-custom');
    if (!accordion) return;
    const key =
      accordion.dataset.filterParamName ||
      accordion.querySelector('.facets__label')?.textContent.trim() ||
      accordion.className;

    if (event.target.open) {
      this._userOpenAccordions.add(key);
    } else {
      this._userOpenAccordions.delete(key);
    }

    // Force the caret to its final state. The theme rotates the icon via
    // `details[open] > summary .icon-animated > svg`, but on some browsers /
    // after section re-renders that rule's animation can play a full circle
    // and settle back to the collapsed direction. An inline transform makes
    // the end state deterministic: open = rotated 180deg (^), closed = 0.
    const svg = event.target.querySelector('summary .icon-animated > svg, summary .icon-caret svg');
    if (svg) svg.style.transform = event.target.open ? 'rotate(180deg)' : 'rotate(0deg)';
  };

  /**
   * Fallback caret sync for browsers without the details `toggle` event
   * (older Safari/WebKit): clicking the summary changes details.open, then a
   * requestAnimationFrame snapshots the new state and pins the svg inline
   * transform the same way handleAccordionToggle does.
   */
  handleFacetSummaryClick = (event) => {
    const summary = event.target.closest('details.facets__panel summary');
    if (!summary) return;
    const details = summary.closest('details');
    if (!details) return;
    window.requestAnimationFrame(() => this.syncCaretIcon(details));
  };

  syncCaretIcon(details) {
    const svg = details.querySelector('summary .icon-animated > svg, summary .icon-caret svg');
    if (svg) svg.style.transform = details.open ? 'rotate(180deg)' : 'rotate(0deg)';
  }

  openRoomsAccordion() {
    const roomsAccordion = Array.from(this.querySelectorAll('.ulif-collection-filters__facets accordion-custom')).find(
      (accordion) => accordion.dataset.filterParamName === ROOMS_FILTER_PARAM_NAME
    );
    const details = roomsAccordion?.querySelector('details');
    if (!(details instanceof HTMLDetailsElement)) return;

    this._userOpenAccordions.add(ROOMS_FILTER_PARAM_NAME);
    details.open = true;
    this.syncCaretIcon(details);
  }

  /**
   * Interval preset chips select their server-rendered value group and submit
   * through the native facet change pipeline. Clicking the active group again
   * clears this facet.
   */
  handleIntervalChipClick = (event) => {
    const chip = event.target.closest('[data-interval-chip]');
    if (!chip) return;

    const param = chip.dataset.param;
    if (!param) return;

    const values = (chip.dataset.values || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
    if (values.length === 0) return;

    const form = chip.closest('form') || this.closest('form');
    if (!form) return;

    const shouldClear = chip.classList.contains('facets__interval-chip--active');

    // Each interval chip is a single-choice preset for this facet. Remove the
    // previous group first; only recreate inputs when selecting a new group.
    form.querySelectorAll(`input[name="${param}"]`).forEach((input) => input.remove());
    if (!shouldClear) {
      values.forEach((value) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = param;
        input.value = value;
        input.dataset.intervalFacetInput = '';
        form.appendChild(input);
      });
    }

    const intervalChips = Array.from(form.querySelectorAll('[data-interval-chip]')).filter(
      (candidate) => candidate.dataset.param === param
    );
    intervalChips.forEach((candidate) => {
      const isActive = !shouldClear && candidate === chip;
      candidate.classList.toggle('facets__interval-chip--active', isActive);
      candidate.setAttribute('aria-pressed', String(isActive));
    });

    const facetsForm = document.querySelector('facets-form-component');
    if (facetsForm?.updateFilters) {
      facetsForm.updateFilters();
    }
  };

  /**
   * Section re-renders replace the accordion subtree with server markup whose
   * vertical accordions default to `open`. Re-apply the user's manual state:
   * accordions they opened stay open, the rest stay collapsed, and the
   * `open-by-default-*` hints (already consumed at init) are stripped so they
   * cannot re-expand anything.
   */
  restoreAccordionState() {
    this.querySelectorAll('.ulif-collection-filters__facets accordion-custom').forEach((accordion) => {
      const key =
        accordion.dataset.filterParamName ||
        accordion.querySelector('.facets__label')?.textContent.trim() ||
        accordion.className;
      const shouldOpen = this._userOpenAccordions.has(key);

      accordion.removeAttribute('open-by-default-on-mobile');
      accordion.removeAttribute('open-by-default-on-desktop');
      accordion.toggleAttribute('open', shouldOpen);

      const details = accordion.querySelector('details');
      if (details) details.open = shouldOpen;

      // Keep the caret's final state deterministic after the swap-in (see
      // handleAccordionToggle for the same inline-transform approach).
      this.syncCaretIcon(details);
    });

    // Force a synchronous style recalc while data-ulf-no-animate is still
    // applied: the open-state change must land in a frame where transitions
    // are disabled, otherwise the @starting-style expand animation can start
    // after the marker has been released (double-rAF is too racy under an
    // idle compositor) and the panel visibly re-expands once.
    void this.offsetHeight;

    // The morph (or its replacement path) marked every facet accordion with
    // data-ulf-no-animate to suppress the expand/collapse transition mid-
    // render. Removing it in the same task as the open-state mutations, or in
    // the very next animation frame, would re-enable the transition before the
    // style recalc of the changed frame and replay the @starting-style
    // animation. Wait two frames: the height change then applies while
    // transitions are still disabled (instant snap), and only afterwards are
    // animations restored for the user's own interactions.
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => {
        this.querySelectorAll('accordion-custom[data-ulf-no-animate]').forEach((accordion) =>
          accordion.removeAttribute('data-ulf-no-animate')
        );
      })
    );

    // Re-bind toggle tracking on the freshly morphed details elements.
    this.querySelectorAll('.ulif-collection-filters__facets accordion-custom details').forEach((details) =>
      details.addEventListener('toggle', this.handleAccordionToggle)
    );
  }

  /**
   * The morph diff can replace or repurpose child nodes (panel, toggle
   * buttons) instead of updating them in place. Cached references to replaced
   * nodes point at detached or semantically different elements, so re-query
   * the live nodes and (only for the changed ones) re-attach event listeners.
   */
  rebind() {
    // Morph can repurpose an unkeyed, same-tag node in place. Such a cached
    // node is still connected, but it no longer represents the control we
    // originally queried. Compare against the live selector result instead of
    // relying on isConnected alone.
    const bindLiveElement = (currentElement, selector, eventName, listener) => {
      const liveElement = this.querySelector(selector);
      if (liveElement === currentElement) return currentElement;

      currentElement?.removeEventListener(eventName, listener);
      liveElement?.addEventListener(eventName, listener);
      return liveElement;
    };

    this.resultsList = this.closest('results-list');
    this.initGridMorphObserver();
    this.panel = bindLiveElement(
      this.panel,
      '[data-ulif-filter-panel]',
      'transitionend',
      this.handlePanelTransitionEnd
    );
    this.desktopToggleButton = bindLiveElement(
      this.desktopToggleButton,
      '[data-ulif-filter-toggle]',
      'click',
      this.handleDesktopToggle
    );
    this.mobileToggleButton = bindLiveElement(
      this.mobileToggleButton,
      '[data-ulif-mobile-filter-toggle]',
      'click',
      this.handleMobileToggle
    );
    this.closeButton = bindLiveElement(
      this.closeButton,
      '[data-ulif-filter-close]',
      'click',
      this.handleClose
    );

    // Grid view buttons live inside the panel and may likewise be repurposed
    // while remaining connected. Compare the complete live node list.
    const liveGridButtons = Array.from(this.querySelectorAll('[data-ulif-grid-view]')).filter(
      (button) => button instanceof HTMLButtonElement
    );
    const gridButtonsChanged =
      liveGridButtons.length !== this.gridViewButtons.length ||
      liveGridButtons.some((button, index) => button !== this.gridViewButtons[index]);

    if (gridButtonsChanged) {
      this.gridViewButtons.forEach((button) => button.removeEventListener('click', this.handleGridViewClick));
      this.gridViewButtons = liveGridButtons;
      this.gridViewButtons.forEach((button) => button.addEventListener('click', this.handleGridViewClick));
    }
  }

  connectedCallback() {
    this.desktopToggleButton = this.querySelector('[data-ulif-filter-toggle]');
    this.mobileToggleButton = this.querySelector('[data-ulif-mobile-filter-toggle]');
    this.closeButton = this.querySelector('[data-ulif-filter-close]');
    this.panel = this.querySelector('[data-ulif-filter-panel]');
    this.resultsList = this.closest('results-list');
    this.mobileMedia = window.matchMedia('(max-width: 749px)');
    this.sidebarMedia = window.matchMedia('(min-width: 1200px)');
    this.isPanelOpen = false;
    this.isClosing = false;
    this.lastTrigger = null;
    this.restoreFocusOnClose = false;
    this.panelAnimationFrame = null;
    this.gridAnimationFrame = null;
    this.gridAnimationTimer = null;
    this.gridEntranceAnimationFrame = null;
    this.gridEntranceAnimationGrid = null;
    this.gridEntranceAnimationTarget = null;
    this.gridEntranceAnimationEndHandler = null;
    this._awaitingMobileGridEntrance = false;
    this.gridViewButtons = Array.from(this.querySelectorAll('[data-ulif-grid-view]')).filter(
      (button) => button instanceof HTMLButtonElement
    );

    // Track which facet accordions the user opened manually. Section
    // re-renders (morph) replace the whole accordion subtree with server HTML
    // whose vertical accordions carry `open`, so we restore the user's actual
    // choices afterwards instead of letting every dropdown expand.
    this._userOpenAccordions = new Set();
    this.querySelectorAll('.ulif-collection-filters__facets accordion-custom details').forEach((details) =>
      details.addEventListener('toggle', this.handleAccordionToggle)
    );

    // Initial caret state sync: the theme rotates the icon via the details
    // [open] rule; after the component collapses the default-open accordions
    // during init, inline-transform each caret so arrows and state agree.
    this.querySelectorAll('.ulif-collection-filters__facets accordion-custom details').forEach((details) =>
      this.syncCaretIcon(details)
    );

    this.syncGridDensityControls();
    this.gridViewButtons.forEach((button) => button.addEventListener('click', this.handleGridViewClick));

    if (!this.panel || (!this.desktopToggleButton && !this.mobileToggleButton)) return;

    this.storageKey = `ulif-collection-filter-panel:${this.dataset.sectionId || 'collection'}:desktop`;
    this.desktopToggleButton?.addEventListener('click', this.handleDesktopToggle);
    this.mobileToggleButton?.addEventListener('click', this.handleMobileToggle);
    this.closeButton?.addEventListener('click', this.handleClose);
    this.addEventListener('filter:update', this.handleFilterUpdate);
    this.addEventListener('click', this.handleIntervalChipClick);
    this.addEventListener('click', this.handleFacetSummaryClick);
    this.panel.addEventListener('transitionend', this.handlePanelTransitionEnd);
    document.addEventListener('keydown', this.handleKeydown);
    this.mobileMedia.addEventListener('change', this.handleViewportChange);
    this.sidebarMedia.addEventListener('change', this.handleSidebarViewportChange);
    this.resetFacetAccordions();

    const isRoomFilterEntry = window.location.hash === ROOM_FILTER_ENTRY_HASH;
    const savedState = this.readSavedState();
    const shouldOpen =
      isRoomFilterEntry ||
      (!this.mobileMedia.matches &&
        (savedState === null ? this.dataset.defaultOpen === 'true' && this.sidebarMedia.matches : savedState));

    this.setOpen(shouldOpen, { animate: false, persist: false });
    if (isRoomFilterEntry) this.openRoomsAccordion();

    // Watch for morph-driven attribute/subtree changes (section re-renders)
    // and restore the open state + rebind repurposed nodes afterwards.
    this.initMorphObserver();
    this.initGridMorphObserver();
  }

  disconnectedCallback() {
    this._morphObserver?.disconnect();
    this._morphObserver = null;
    this.desktopToggleButton?.removeEventListener('click', this.handleDesktopToggle);
    this.mobileToggleButton?.removeEventListener('click', this.handleMobileToggle);
    this.closeButton?.removeEventListener('click', this.handleClose);
    this.panel?.removeEventListener('transitionend', this.handlePanelTransitionEnd);
    document.removeEventListener('keydown', this.handleKeydown);
    this.mobileMedia?.removeEventListener('change', this.handleViewportChange);
    this.sidebarMedia?.removeEventListener('change', this.handleSidebarViewportChange);
    this.gridViewButtons?.forEach((button) => button.removeEventListener('click', this.handleGridViewClick));
    window.cancelAnimationFrame(this.panelAnimationFrame);
    window.cancelAnimationFrame(this.gridAnimationFrame);
    window.clearTimeout(this.gridAnimationTimer);
    this.cleanupGridAnimation();
  }

  handleDesktopToggle = (event) => {
    this.lastTrigger = event.currentTarget;
    this.setOpen(!this.isPanelOpen, { persist: true });
  };

  handleMobileToggle = (event) => {
    this.lastTrigger = event.currentTarget;
    this.setOpen(!this.isPanelOpen, { persist: false });
  };

  handleClose = () => {
    this.setOpen(false, {
      persist: !this.mobileMedia.matches,
      restoreFocus: true,
    });
  };

  handleKeydown = (event) => {
    if (event.key !== 'Escape' || !this.isPanelOpen) return;
    event.preventDefault();
    this.handleClose();
  };

  handleGridViewClick = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;

    const view = button.dataset.ulifGridView;
    if (!['default', 'mobile-single'].includes(view)) return;

    const grid = this.resultsList?.querySelector('.product-grid');
    if (!grid) return;

    if (typeof this.resultsList?.updateLayout === 'function') {
      const layoutInput = document.createElement('input');
      layoutInput.type = 'radio';
      layoutInput.value = view;
      this.resultsList.updateLayout({ target: layoutInput });
    } else {
      grid.setAttribute('product-grid-view', view);
      try {
        sessionStorage.setItem('product-grid-view-mobile', view);
      } catch (_) {
        // Storage can be unavailable in private browsing contexts.
      }
    }

    this.gridViewButtons.forEach((viewButton) => {
      viewButton.setAttribute('aria-pressed', String(viewButton === button));
    });
  };

  handleViewportChange = () => {
    this.setOpen(false, { animate: false, persist: false });
    this.syncGridDensityControls();

    if (this.mobileMedia.matches) return;

    const savedState = this.readSavedState();
    if (savedState === true) this.setOpen(true, { animate: false, persist: false });
  };

  handleSidebarViewportChange = () => {
    if (this.isPanelOpen && !this.mobileMedia.matches) this.syncSidebarLayout();
  };

  handlePanelTransitionEnd = (event) => {
    if (event.target !== this.panel) return;

    if (this.mobileMedia.matches && event.propertyName === 'height') {
      this.panel.classList.remove('panel--animating');

      if (this.isClosing) {
        this.finalizeClose();
        return;
      }

      if (this.isPanelOpen) {
        this.panel.style.height = 'auto';
        this.panel.style.overflow = 'visible';
      }
      return;
    }

    if (!this.mobileMedia.matches && event.propertyName === 'transform' && this.isClosing) {
      this.finalizeClose();
    }
  };

  resetFacetAccordions() {
    this.querySelectorAll('.ulif-collection-filters__panel accordion-custom').forEach((accordion) => {
      accordion.removeAttribute('open');
      accordion.removeAttribute('open-by-default-on-mobile');
      accordion.removeAttribute('open-by-default-on-desktop');
      accordion.removeAttribute('data-disable-animation-on-desktop');
      accordion.removeAttribute('data-close-with-escape');
      const details = accordion.querySelector('details');
      if (details instanceof HTMLDetailsElement) {
        details.removeAttribute('data-auto-close-details');
        details.open = false;
      }

      accordion.querySelectorAll('floating-panel-component').forEach((panel) => {
        panel.removeAttribute('data-close-on-resize');
        panel.style.removeProperty('top');
      });
    });
  }

  closePanelDetails() {
    this.panel?.querySelectorAll('details[open]').forEach((details) => {
      if (details instanceof HTMLDetailsElement) details.open = false;
    });
  }

  syncGridDensityControls() {
    const grid = this.resultsList?.querySelector('.product-grid');
    const options = this.gridViewButtons || [];

    if (!grid || options.length === 0) return;

    const validViews = ['default', 'mobile-single'];
    let view = grid.getAttribute('product-grid-view') || this.dataset.mobileGridView || 'default';

    try {
      const storedView = sessionStorage.getItem('product-grid-view-mobile');
      if (storedView && validViews.includes(storedView)) view = storedView;
    } catch (_) {
      // Storage can be unavailable in private browsing contexts.
    }

    if (!validViews.includes(view)) view = this.dataset.mobileGridView || 'default';
    grid.setAttribute('product-grid-view', view);
    options.forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.ulifGridView === view));
    });
  }

  setOpen(open, { animate = true, persist = true, restoreFocus = false } = {}) {
    if (!this.panel) return;

    window.cancelAnimationFrame(this.panelAnimationFrame);
    this.restoreFocusOnClose = restoreFocus;

    if (persist && !this.mobileMedia.matches) this.writeSavedState(open);

    if (open) {
      this.openPanel(animate);
      return;
    }

    this.closePanel(animate);
  }

  openPanel(animate) {
    this.isPanelOpen = true;
    this.isClosing = false;
    this.syncToggleState(true);
    this.panel.hidden = false;
    this.panel.removeAttribute('inert');
    this.panel.setAttribute('aria-hidden', 'false');

    if (this.mobileMedia.matches) {
      this.resultsList?.removeAttribute('data-ulif-sidebar-open');
      this.openMobilePanel(animate);
      return;
    }

    this.clearMobilePanelStyles();
    this.animateGridLayout(() => {
      // data-panel-open must be applied inside the FLIP mutate callback so the
      // grid column change happens between the two rect measurements. Applying
      // it earlier (before animateGridLayout) made the layout jump instantly
      // and the FLIP had nothing to animate.
      this.toggleAttribute('data-panel-open', true);
      const sidebarIsActive = this.dataset.panelLayout === 'sidebar' && this.sidebarMedia.matches;
      this.resultsList?.toggleAttribute('data-ulif-sidebar-open', sidebarIsActive);
    }, animate);

    if (!animate || this.prefersReducedMotion()) {
      this.panel.setAttribute('data-ulif-panel-visible', '');
      return;
    }

    this.panelAnimationFrame = window.requestAnimationFrame(() => {
      if (this.isPanelOpen) this.panel.setAttribute('data-ulif-panel-visible', '');
    });
  }

  openMobilePanel(animate) {
    const currentHeight = this.panel.getBoundingClientRect().height;
    this.panel.style.height = `${currentHeight}px`;
    this.panel.style.overflow = 'hidden';

    if (!animate || this.prefersReducedMotion()) {
      this.panel.setAttribute('data-ulif-panel-visible', '');
      this.panel.style.height = 'auto';
      this.panel.style.overflow = 'visible';
      this.focusPanelCloseButton();
      return;
    }

    // The height transition is only enabled while this class is present, so
    // section re-renders (morph) cannot accidentally replay the animation.
    this.panel.classList.add('panel--animating');
    this.panel.getBoundingClientRect();
    this.panelAnimationFrame = window.requestAnimationFrame(() => {
      if (!this.isPanelOpen) return;
      this.panel.setAttribute('data-ulif-panel-visible', '');
      this.panel.style.height = `${this.panel.scrollHeight}px`;
      this.focusPanelCloseButton();
    });
  }

  closePanel(animate) {
    this.isPanelOpen = false;
    this.syncToggleState(false);
    this.closePanelDetails();
    this.panel.setAttribute('aria-hidden', 'true');
    this.panel.setAttribute('inert', '');

    if (this.mobileMedia.matches) {
      this.closeMobilePanel(animate);
      return;
    }

    const wasVisible = this.panel.hasAttribute('data-ulif-panel-visible');
    this.panel.removeAttribute('data-ulif-panel-visible');

    if (!animate || !wasVisible || this.prefersReducedMotion()) {
      this.finalizeClose();
      return;
    }

    this.isClosing = true;
  }

  closeMobilePanel(animate) {
    const wasVisible = this.panel.hasAttribute('data-ulif-panel-visible');
    const currentHeight = this.panel.getBoundingClientRect().height;

    this.panel.style.height = `${currentHeight}px`;
    this.panel.style.overflow = 'hidden';
    this.panel.getBoundingClientRect();
    this.panel.removeAttribute('data-ulif-panel-visible');

    if (!animate || !wasVisible || currentHeight === 0 || this.prefersReducedMotion()) {
      this.finalizeClose();
      return;
    }

    this.isClosing = true;
    this.panel.classList.add('panel--animating');
    this.panelAnimationFrame = window.requestAnimationFrame(() => {
      this.panel.style.height = '0px';
    });
  }

  finalizeClose() {
    if (!this.panel) return;

    const wasDesktopSidebar = !this.mobileMedia.matches && this.resultsList?.hasAttribute('data-ulif-sidebar-open');
    const removeLayoutState = () => {
      this.toggleAttribute('data-panel-open', false);
      this.resultsList?.removeAttribute('data-ulif-sidebar-open');
    };

    this.isClosing = false;
    this.panel.removeAttribute('data-ulif-panel-visible');
    this.panel.classList.remove('panel--animating');
    this.clearMobilePanelStyles();

    if (wasDesktopSidebar) {
      this.animateGridLayout(() => {
        // Hide the panel inside the FLIP mutate callback: hiding it before the
        // first rect measurement empties the 'filters' grid area, which makes
        // the product grid auto-place into the filters column and the FLIP
        // measures a wrong starting rect (no visible animation).
        this.panel.hidden = true;
        removeLayoutState();
      });
    } else {
      this.panel.hidden = true;
      removeLayoutState();
    }

    if (this.restoreFocusOnClose && this.lastTrigger instanceof HTMLElement) {
      const focusTarget = this.lastTrigger;
      window.requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
    }

    this.restoreFocusOnClose = false;
  }

  clearMobilePanelStyles() {
    this.panel.style.height = '';
    this.panel.style.overflow = '';
  }

  syncToggleState(open) {
    this.desktopToggleButton?.setAttribute('aria-expanded', String(open));
    this.mobileToggleButton?.setAttribute('aria-expanded', String(open));
  }

  syncSidebarLayout() {
    const sidebarIsActive =
      this.isPanelOpen &&
      !this.mobileMedia.matches &&
      this.dataset.panelLayout === 'sidebar' &&
      this.sidebarMedia.matches;

    this.setSidebarLayout(sidebarIsActive);
  }

  setSidebarLayout(active, animate = true) {
    if (!this.resultsList) return;

    const alreadyActive = this.resultsList.hasAttribute('data-ulif-sidebar-open');
    if (alreadyActive === active) return;

    this.animateGridLayout(() => {
      this.resultsList.toggleAttribute('data-ulif-sidebar-open', active);
    }, animate);
  }

  focusPanelCloseButton() {
    if (!this.mobileMedia.matches || !(this.closeButton instanceof HTMLElement)) return;
    this.closeButton.focus({ preventScroll: true });
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  animateGridLayout(mutateLayout, animate = true) {
    const mainGrid = this.resultsList?.querySelector('.main-collection-grid');

    if (!mainGrid) {
      mutateLayout();
      return;
    }

    if (!animate || this.prefersReducedMotion()) {
      mutateLayout();
      return;
    }

    const firstRect = mainGrid.getBoundingClientRect();
    mutateLayout();
    const lastRect = mainGrid.getBoundingClientRect();

    if (firstRect.width === 0 || lastRect.width === 0) return;

    const translateX = firstRect.left - lastRect.left;
    const translateY = firstRect.top - lastRect.top;
    const scaleX = firstRect.width / lastRect.width;

    mainGrid.style.transition = 'none';
    mainGrid.style.transformOrigin = 'top left';
    mainGrid.style.willChange = 'transform, opacity';
    mainGrid.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scaleX(${scaleX})`;
    mainGrid.style.opacity = '0.96';

    // Force a synchronous reflow so the start frame (transition: none) is
    // actually painted before we queue the transition target on the next
    // frame. Without this, browsers can coalesce the start and end states
    // into one paint, which makes the FLIP animation invisible.
    mainGrid.offsetHeight;

    window.cancelAnimationFrame(this.gridAnimationFrame);
    window.clearTimeout(this.gridAnimationTimer);
    this.gridAnimationFrame = window.requestAnimationFrame(() => {
      mainGrid.style.transition = 'transform 320ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms ease';
      mainGrid.style.transform = 'translate3d(0, 0, 0) scaleX(1)';
      mainGrid.style.opacity = '1';

      this.gridAnimationTimer = window.setTimeout(() => {
        this.cleanupGridAnimation(mainGrid);
      }, 340);
    });
  }

  cleanupGridAnimation(mainGrid = this.resultsList?.querySelector('.main-collection-grid')) {
    if (!mainGrid) return;
    mainGrid.style.transition = '';
    mainGrid.style.transform = '';
    mainGrid.style.transformOrigin = '';
    mainGrid.style.willChange = '';
    mainGrid.style.opacity = '';
  }

  readSavedState() {
    try {
      const savedValue = sessionStorage.getItem(this.storageKey);
      return savedValue === null ? null : savedValue === 'true';
    } catch (_) {
      return null;
    }
  }

  writeSavedState(open) {
    try {
      sessionStorage.setItem(this.storageKey, String(open));
    } catch (_) {
      // Storage can be unavailable in private browsing contexts.
    }
  }
}

if (!customElements.get('ulif-collection-filters')) {
  customElements.define('ulif-collection-filters', UlifCollectionFilters);
}
